import { db } from "../config/firebase";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import AuthManager from "../managers/AuthManager";
import { DEFAULT_YEAR, toDivisionInt } from "../config/constants";

class ScoutingReportManager {
  constructor() {
    this.reportsRef = collection(db, "scoutingReports");
  }

  async createReport(reportData) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const userId = AuthManager.getCurrentUser()?.uid;
      if (!userId) throw new Error("User must be authenticated");

      const now = new Date().toISOString();
      const report = {
        ...reportData,
        division: toDivisionInt(reportData.division),
        year: reportData.year || DEFAULT_YEAR,
        userId,
        dateCreated: now,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(this.reportsRef, report);
      return { id: docRef.id, ...report };
    } catch (error) {
      console.error("Error creating report:", error);
      throw error;
    }
  }

  async getReportById(reportId) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const userId = AuthManager.getCurrentUser()?.uid;
      if (!userId) throw new Error("User must be authenticated");

      const reportRef = doc(this.reportsRef, reportId);
      const reportSnap = await getDoc(reportRef);

      if (!reportSnap.exists()) {
        throw new Error("Report not found");
      }

      const reportData = reportSnap.data();

      if (reportData.userId !== userId) {
        throw new Error("Unauthorized access to report");
      }

      return {
        id: reportId,
        ...reportData,
        division: toDivisionInt(reportData.division),
        year: reportData.year || DEFAULT_YEAR,
      };
    } catch (error) {
      console.error("Error getting report:", error);
      throw error;
    }
  }

  async getUserReports() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const userId = AuthManager.getCurrentUser()?.uid;
      if (!userId) throw new Error("User must be authenticated");

      const q = query(this.reportsRef, where("userId", "==", userId));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          division: toDivisionInt(data.division ),
          year: data.year || DEFAULT_YEAR,
          dateCreated: data.dateCreated || data.createdAt,
        };
      });
    } catch (error) {
      console.error("Error getting user reports:", error);
      throw error;
    }
  }

  async updateReport(reportId, updateData) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const userId = AuthManager.getCurrentUser()?.uid;
      if (!userId) throw new Error("User must be authenticated");

      const reportRef = doc(this.reportsRef, reportId);

      await updateDoc(reportRef, {
        ...updateData,
        division: toDivisionInt(updateData.division),
        year: updateData.year || DEFAULT_YEAR,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating report:", error);
      throw error;
    }
  }

  async deleteReport(reportId) {
    try {
      const userId = AuthManager.getCurrentUser()?.uid;
      if (!userId) {
        throw new Error("User must be authenticated");
      }

      const reportRef = doc(this.reportsRef, reportId);
      const snapshot = await getDocs(
        query(this.reportsRef, where("userId", "==", userId))
      );

      const reportExists = snapshot.docs.some((doc) => doc.id === reportId);
      if (!reportExists) {
        throw new Error("Report not found or unauthorized");
      }

      await deleteDoc(reportRef);

      return { success: true };
    } catch (error) {
      console.error("Error deleting report:", error);
      throw error;
    }
  }
}

const manager = new ScoutingReportManager();
export default manager;
